import { DescriptionOutlined, ImageOutlined } from "@mui/icons-material";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import React, { useContext, useEffect, useRef, useState } from "react";

import CalendarClock from "@/components/Icons/CalendarClock.tsx";
import ChannelDialOutlined from "@/components/Icons/ChannelDialOutlined.tsx";
import NoteEditOutlined from "@/components/Icons/NoteEditOutlined.tsx";
import SimpleModal from "@/components/SimpleModal.tsx";
import WorkflowModalData from "@/components/WorkflowModalData.tsx";
import { displayArtifactQuery, rawFilesQuery, transcriptionsQuery } from "@/components/workflowUtils";
import { deleteWorkflowItem } from "@/models/actions/clientActions.ts";
import { DataContext } from "@/models/DataProvider.tsx";
import { useReactiveRelationalQuery } from "@/models/hooks/useReactiveRelationalQuery.ts";
import { selectTranscription } from "@/utils";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";

export default function WorkflowItem({ workflowItem }: { workflowItem: any }) {
  /**
   * Initial Values
   */
  const { preferredLanguage } = useContext(DataContext);
  const theme = useTheme();
  const contentId = workflowItem?.contentId;

  /**
   * Local State Changes (Not Workflow Related)
   */
  const [disabled, setDisabled] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const workflowItemRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [confirmClose, setConfirmClose] = useState<boolean>(false);

  const handleConfirmDelete = async () => {
    try {
      setDisabled(() => true);
      await deleteWorkflowItem(workflowItem.workspaceId, workflowItem?.id);
    } catch (e) {
      console.log(e);
    } finally {
      setDisabled(() => false);
    }
  };

  /**
   * Fetch Data
   */
  const transcriptions =
    useReactiveRelationalQuery(transcriptionsQuery, [contentId], "WorkflowScheduledItem:transcriptionsForContent") ??
    [];

  const rawFiles =
    useReactiveRelationalQuery(rawFilesQuery, [contentId], "WorkflowScheduledItem:filesForContent") ?? [];

  const displayArtifact = useReactiveRelationalQuery(
    displayArtifactQuery,
    [contentId],
    "WorkflowScheduledItem:displayArtifactForContent",
  );

  const { simpleHtml, richTranscript, textTranscript } = selectTranscription(preferredLanguage, transcriptions as any);
  const transcriptionContent = simpleHtml || richTranscript || textTranscript;
  const file = rawFiles?.[0];

  /**
   * Localized Components
   */
  const fileType = () => {
    if (file) {
      return (file?.mimeType as string)?.split("/")?.[0];
    }
    return transcriptionContent ? "text" : "";
  };
  const FileIcon = () => {
    let icon = <DescriptionOutlined role="presentation" />;
    switch (fileType()) {
      case "image":
        icon = <ImageOutlined role="presentation" />;
        break;
      case "text":
        icon = <NoteEditOutlined role="presentation" />;
        break;
    }
    return icon;
  };

  const FileOrTranscriptionContent = () => {
    if (file?.name) {
      return (
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: `${boxWidth}px`,
          }}
        >
          {file?.name}
        </Typography>
      );
    } else if (transcriptionContent?.transcriptionContent) {
      return (
        <div
          id={`${contentId}-transcription`}
          role="textbox"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: `${boxWidth}px`,
            height: "22px",
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForDisplay(transcriptionContent?.transcriptionContent ?? ""),
          }}
        />
      );
    }
    return <Box sx={{ color: theme.palette.secondary.light }}>loading...</Box>;
  };

  /**
   * Dynamically set the width for truncating text
   */
  useEffect(() => {
    try {
      const observer = new ResizeObserver(() => {
        setBoxWidth(() => workflowItemRef?.current?.offsetWidth - 80);
      });
      observer.observe(workflowItemRef.current);
      return () => {
        if (observer && workflowItemRef.current) observer.unobserve(workflowItemRef.current);
      };
    } catch (e) {
      console.error("ResizeObserver Error", { e });
    }
  }, []);

  return (
    <>
      <Box
        ref={workflowItemRef}
        sx={{
          p: 2,
          minHeight: "150px",
          position: "relative",
          borderRadius: 2,
          background: theme.palette.secondary.dark,
          border: `solid 1px ${theme.palette.secondary.main}`,
          "&:hover": {
            border: `solid 1px ${theme.palette.secondary.light}`,
          },
        }}
      >
        {confirmDelete && (
          <Box
            sx={{
              p: 2,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(38,41,44,0.7)",
              border: "solid 1px red",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              backdropFilter: "blur(4px)",
            }}
          >
            <Box>
              <Box sx={{ mb: 2, textAlign: "center" }}>Are you sure you want to delete this scheduled message?</Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Button disabled={disabled} onClick={() => setConfirmDelete(() => false)} variant="outlined">
                    Cancel
                  </Button>
                </Box>
                <Box>
                  <Button disabled={disabled} variant="contained" color="error" onClick={() => handleConfirmDelete()}>
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mb: 2,
            borderBottom: `solid 1px ${theme.palette.secondary.light}`,
            paddingBottom: 1,
            display: "flex",
            alignItems: "start",
            gap: 1,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                fontWeight: "bold",
                fontSize: "1.25rem",
              }}
            >
              {workflowItem?.displayName}
            </Box>
            <Box sx={{ color: theme.palette.neutral.main }}>
              {displayArtifact?.id && (
                <>
                  {displayArtifact?.title && workflowItem?.displayName !== displayArtifact?.title && (
                    <Box>{displayArtifact.title}</Box>
                  )}
                  {displayArtifact?.description && <Box>{displayArtifact.description}</Box>}
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box>
              <Box
                onClick={() => setModalOpen((value) => !value)}
                component="button"
                sx={{
                  background: theme.palette.secondary.main,
                  "&:hover": { background: theme.palette.secondary.light },
                  fontSize: "14px",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  py: 0.5,
                  px: 1,
                }}
                className="basic-button full-width"
              >
                Edit
              </Box>
            </Box>
            <Box>
              <Box
                onClick={() => setConfirmDelete(() => true)}
                component="button"
                sx={{
                  background: theme.palette.secondary.main,
                  "&:hover": { background: theme.palette.error.main },
                  fontSize: "14px",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  py: 0.5,
                  px: 1,
                }}
                className="basic-button full-width"
              >
                Delete
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 1, display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ display: "flex", color: theme.palette.neutral.main }}>
            <FileIcon />
          </Box>
          <Box>
            <FileOrTranscriptionContent />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box sx={{ display: "flex", color: theme.palette.neutral.main }}>
            <CalendarClock role="presentation" />
          </Box>
          <Box>{workflowItem?.prettyDate}</Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
          <Box sx={{ display: "flex", color: theme.palette.neutral.main }}>
            <ChannelDialOutlined />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {workflowItem?.recipientMap?.map((name) => (
              <Chip key={`chip-${workflowItem.id}-${name}`} label={name} size="small" />
            ))}
            {workflowItem?.andMore ? <Box sx={{ fontSize: "14px" }}>{workflowItem.andMore}</Box> : ""}
          </Box>
        </Box>
      </Box>

      <SimpleModal
        open={modalOpen}
        title="Edit Scheduled Message"
        closeModal={() => {
          if (hasChanges) {
            setConfirmClose(() => true);
            setTimeout(() => {
              setConfirmClose(() => false);
            }, 200);
          } else {
            setModalOpen(() => false);
          }
        }}
        sx={{ position: "relative", boxShadow: hasChanges ? `0 0 0 2px ${theme.palette.info.main}` : "" }}
      >
        <WorkflowModalData
          askToConfirmClose={confirmClose}
          setHasChanges={(value: boolean) => setHasChanges(() => value)}
          hiddenTabs={["media", "generate"]}
          closeModal={() => setModalOpen(() => false)}
          activeTabState="date"
          workflowItem={workflowItem}
        />
      </SimpleModal>
    </>
  );
}
